/** @forge-header
 * @id          tickerHelpers
 * @role        Shared ticker parsing utilities | team lookups, player parsing, fallback
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,PlayerInfo,TickerCategory
 * @downstream  kalshiTicker/sports/* | kalshiTicker/parsers/*
 * @exports     labelToSport:fn | parseFallback:fn | parsePlayerFromMarket:fn | formatPlayerName:fn | formatTennisPlayerCode:fn | nflTeamFromCode:fn | nhlTeamFromCode:fn | mlbTeamFromCode:fn
 * @test        parse.test.ts (indirect)
 */

import { MLB_TEAM_NAMES, NFL_TEAM_NAMES, NHL_TEAM_NAMES } from './teamNames';
import type { ParsedTicker, PlayerInfo, TickerCategory } from './types';

/** Parser function signature */
export type TickerParser = (raw: string) => ParsedTicker;

/**
 * Map parser label strings to sport keys for teamNameFromCode lookups.
 * NCAA labels return null (no lookup table for college teams).
 */
export function labelToSport(label: string): string | null {
  switch (label) {
    case 'NBA':
      return 'nba';
    case 'NFL':
      return 'nfl';
    case 'NHL':
      return 'nhl';
    case 'MLB':
      return 'mlb';
    default:
      return null; // NCAA, etc. — no lookup table
  }
}

/** Default fallback for unrecognized tickers */
export function parseFallback(raw: string): ParsedTicker {
  const firstDash = raw.indexOf('-');
  const series = firstDash > 0 ? raw.slice(0, firstDash) : raw;

  let category: TickerCategory = 'unknown';
  if (series.startsWith('KXATTY') || series.startsWith('KXPRES')) {
    category = 'politics';
  } else if (series.startsWith('KXBTC') || series.startsWith('KXETH')) {
    category = 'crypto';
  }

  return {
    raw,
    category,
    series,
    date: null,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: null,
    shortLabel: raw,
    fullLabel: raw,
  };
}

/**
 * Parse player info from market part: SEAABARNER88 -> { teamCode: SEA, playerId: ABARNER, jersey: 88 }
 */
export function parsePlayerFromMarket(marketPart: string): PlayerInfo | null {
  if (marketPart.length < 4) return null;

  const teamCode = marketPart.slice(0, 3);
  const rest = marketPart.slice(3);

  const match = rest.match(/^([A-Z]+)(\d+)?$/);
  if (!match) {
    return {
      teamCode,
      playerId: rest,
      displayName: formatPlayerName(rest),
    };
  }

  const playerId = match[1] ?? rest;
  const jerseyNumber = match[2] ? Number(match[2]) : undefined;

  return {
    teamCode,
    playerId,
    jerseyNumber,
    displayName: formatPlayerName(playerId, jerseyNumber),
  };
}

/** Format player ID into display name: ABARNER -> A. Barner */
export function formatPlayerName(playerId: string, jersey?: number): string {
  if (!playerId) return '';

  const initial = playerId.charAt(0);
  const lastName = playerId.slice(1);
  const formatted = lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase();

  const name = `${initial}. ${formatted}`;
  return jersey !== undefined ? `${name} #${jersey}` : name;
}

/**
 * Format tennis player code to readable name
 * DJO -> Djokovic, SINNER -> Sinner, etc.
 */
export function formatTennisPlayerCode(code: string): string {
  if (!code) return '';
  return code.charAt(0).toUpperCase() + code.slice(1).toLowerCase();
}

/** NFL team code to name mapping */
export function nflTeamFromCode(code: string): string | null {
  return NFL_TEAM_NAMES[code] ?? null;
}

/** MLB team code to name mapping (Kalshi 2-3 letter codes) */
export function mlbTeamFromCode(code: string): string | null {
  return MLB_TEAM_NAMES[code] ?? null;
}

/** NHL team code to name mapping */
export function nhlTeamFromCode(code: string): string | null {
  return NHL_TEAM_NAMES[code] ?? null;
}
